import { Capacitor } from '@capacitor/core'
import { FacebookEvents } from 'capacitor-facebook-events'
import { trackMetaPixelCustomEvent, trackMetaPixelEvent } from './metaPixel'
import { META_CONVERSIONS_ENDPOINT } from '@/config/endpoints'

const MAX_META_EVENT_NAME_LENGTH = 50

const cleanParams = (params?: Record<string, unknown>) => {
  if (!params) return undefined
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  )
}

// In-memory cache of hashed user identity — set once on auth, reused on every event
let cachedMetaUserData: { em?: string; external_id?: string } = {}

const sha256Hex = async (value: string): Promise<string> => {
  const encoded = new TextEncoder().encode(value.trim().toLowerCase())
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded)
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Call once when the user authenticates (login or app resume).
 * Hashes email + uid and caches them so every subsequent standard event
 * includes proper user identity for Meta's Event Match Quality scoring.
 */
export const setMetaUserIdentity = async (email: string | null, uid: string | null): Promise<void> => {
  cachedMetaUserData = {}

  if (email) {
    cachedMetaUserData.em = await sha256Hex(email)
  }
  if (uid) {
    cachedMetaUserData.external_id = await sha256Hex(uid)
  }

  console.log('[Meta Events] User identity set', {
    hasEmail: !!cachedMetaUserData.em,
    hasExternalId: !!cachedMetaUserData.external_id,
  })
}

const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

const sendServerSideConversionEvent = async (
  eventName: string,
  eventId: string,
  params?: Record<string, unknown>
): Promise<void> => {
  try {
    const body: Record<string, unknown> = {
      event_name: eventName,
      event_id: eventId,
      hashed_email: cachedMetaUserData.em,
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      action_source: Capacitor.isNativePlatform() ? 'app' : 'website',
      ...params,
    }

    // Pass the raw UID separately so the backend can hash it independently
    // We store the hashed UID in cachedMetaUserData.external_id but we need the raw one
    // for the server — so we pull it from Firebase Auth directly here
    const { auth } = await import('@/lib/firebase')
    if (auth.currentUser?.uid) {
      body.user_id = auth.currentUser.uid
    }

    await fetch(META_CONVERSIONS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (error) {
    // Non-critical — never block the user flow
    console.error('[Meta CAPI] Server-side event failed:', error)
  }
}

/**
 * Native Facebook SDK event name strings for standard events.
 * These differ from the web pixel names (e.g. 'Lead' vs 'fb_mobile_lead').
 */
const NATIVE_STANDARD_EVENT_NAMES: Record<string, string> = {
  Lead: 'fb_mobile_lead',
  Purchase: 'fb_mobile_purchase',
  InitiateCheckout: 'fb_mobile_initiated_checkout',
  CompleteRegistration: 'fb_mobile_complete_registration',
  ViewContent: 'fb_mobile_content_view',
  Search: 'fb_mobile_search',
  AddToCart: 'fb_mobile_add_to_cart',
  AddToWishlist: 'fb_mobile_add_to_wishlist',
}

/**
 * Send a Meta standard event (like Lead, Purchase) to both client and server.
 *
 * Client side:
 *   - Web: fbq('track', ...) with hashed userData + eventID for deduplication
 *   - Native: Facebook SDK logEvent with hashed identity params
 *
 * Server side (always, regardless of platform):
 *   - Meta Conversions API via our backend, using hashed email + uid for
 *     attribution that works even when ATT is denied or the pixel is blocked
 *
 * Meta deduplicates the two signals using the shared event_id.
 */
export const sendMetaStandardEvent = async (
  standardEventName: string,
  params?: Record<string, unknown>
): Promise<void> => {
  const cleanedParams = cleanParams(params)
  const platform = Capacitor.isNativePlatform() ? 'NATIVE' : 'WEB'
  const eventId = crypto.randomUUID()

  console.log(`[Meta Events] Sending standard event (${platform}):`, {
    standardEventName,
    eventId,
    params: cleanedParams || {},
    hasUserIdentity: !!(cachedMetaUserData.em || cachedMetaUserData.external_id),
  })

  // Always fire server-side in parallel — this is the iOS-ATT-proof path
  void sendServerSideConversionEvent(standardEventName, eventId, cleanedParams as Record<string, unknown>)

  if (Capacitor.isNativePlatform()) {
    const nativeEventName = NATIVE_STANDARD_EVENT_NAMES[standardEventName] ?? standardEventName
    try {
      await FacebookEvents.logEvent({
        event: nativeEventName,
        params: {
          ...cleanedParams,
          ...(cachedMetaUserData.em ? { _ui: cachedMetaUserData.em } : {}),
          ...(cachedMetaUserData.external_id ? { fb_content_id: cachedMetaUserData.external_id } : {}),
          AdvertiserTrackingEnabled: true,
        },
      })
      console.log(`[Meta Events] ✓ Native standard event logged: ${nativeEventName}`)
    } catch (error) {
      console.error(`[Meta Events] ✗ Native standard event failed: ${nativeEventName}`, error)
    }
    return
  }

  try {
    const userData = Object.keys(cachedMetaUserData).length > 0 ? cachedMetaUserData : undefined
    trackMetaPixelEvent(standardEventName, { ...cleanedParams, eventID: eventId } as Record<string, any>, userData)
    console.log(`[Meta Events] ✓ Web standard event sent: ${standardEventName}`)
  } catch (error) {
    console.error(`[Meta Events] ✗ Web standard event failed: ${standardEventName}`, error)
  }
}

/**
 * Send a Meta/Facebook event to both native iOS and web platforms
 * On iOS: Uses capacitor-facebook-events plugin to log to Facebook SDK
 * On Web: Uses Meta Pixel (fbq) for event tracking
 */
export const sendMetaEvent = async (
  eventName: string,
  params?: Record<string, unknown>
): Promise<void> => {
  // Validate event name length
  if (eventName.length > MAX_META_EVENT_NAME_LENGTH) {
    console.warn(`[Meta Events] Event name exceeds ${MAX_META_EVENT_NAME_LENGTH} characters: "${eventName}"`)
    return
  }

  const cleanedParams = cleanParams(params)
  const platform = Capacitor.isNativePlatform() ? 'NATIVE' : 'WEB'

  console.log(`[Meta Events] Sending event (${platform}):`, {
    eventName,
    params: cleanedParams || {}
  })

  // Native iOS/Android: Use Capacitor Facebook Events plugin
  if (Capacitor.isNativePlatform()) {
    try {
      console.log(`[Meta Events] Logging to native Facebook SDK on ${Capacitor.getPlatform()}...`)
      
      await FacebookEvents.logEvent({
        event: eventName,
        params: {
          ...cleanedParams,
          AdvertiserTrackingEnabled: true,
        },
      })
      
      console.log(`[Meta Events] ✓ Native event logged successfully: ${eventName}`)
    } catch (error) {
      console.error(`[Meta Events] ✗ Native event failed: ${eventName}`, error)
      console.error(`[Meta Events] Error details:`, {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      })
    }
    return
  }

  // Web: Use Meta Pixel
  try {
    console.log(`[Meta Events] Logging to Meta Pixel (web)...`)
    trackMetaPixelCustomEvent(eventName, cleanedParams)
    console.log(`[Meta Events] ✓ Web event sent successfully: ${eventName}`)
  } catch (error) {
    console.error(`[Meta Events] ✗ Web event failed: ${eventName}`, error)
    console.error(`[Meta Events] Error details:`, {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    })
  }
}
